import { useEffect, useRef } from "react";

import { PCRS_STATUS } from "features/cdm/constants/pcrsSchemeCheck";
import { useCdmDrafts, useReleaseCdmDrafts } from "features/cdm/hooks/useCdmDrafts";

// A definitive scheme check means PCRS was actually queried (so doctorNum /
// validPatient are meaningful for re-evaluation). Private-skipped / transient
// errors can't drive a release.
const DEFINITIVE_STATUSES = [
  PCRS_STATUS.VALID,
  PCRS_STATUS.INVALID,
  PCRS_STATUS.NO_MEDICAL_CARD,
];

// PCRS <result> dates arrive as strings; normalise dd/MM/yyyy or dd-MM-yyyy to
// yyyy-MM-dd (LocalDate). Unknown/blank → null: by the 1st a prior mid-month
// registration is already effective, so a null start date correctly no longer
// triggers the registration hold (only a genuine panel transfer keeps it held).
const toLocalDate = (value) => {
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);

  const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return null;
};

const toReleaseSchemeCheck = (schemeCheck) => ({
  schemeStartDate: toLocalDate(schemeCheck?.parsedResult?.startDate),
  doctorNum: schemeCheck?.parsedResult?.doctorNum ?? null,
  validPatient: !!schemeCheck?.validPatient,
  invalidReason: schemeCheck?.invalidReason ?? null,
});

// KIN-1236: the BE holds a mid-month / panel-transfer review as a DRAFT until the
// 1st, and release is FE-driven (the BE has no PCRS access). This finds the held
// draft for the current patient and, on/after its holdUntil, re-posts the fresh
// PCRS scheme-check so the BE can release it (or keep it held). Returns the held
// draft (if any) for the banner.
//
// The BE `cdm.midmonth.hold.enable` flag is the single source of truth: with it
// off nothing is ever held, so /cdm/drafts comes back empty and this is
// naturally inert — no mirrored front-end flag to keep in lockstep.
export const useHeldDraftRelease = ({ patientId, schemeCheck }) => {
  const { data: drafts } = useCdmDrafts();
  const { mutate: releaseDrafts } = useReleaseCdmDrafts();
  const attemptedRef = useRef(null);

  const heldDraft = (drafts ?? []).find(
    (draft) => String(draft.patientId) === String(patientId)
  );

  useEffect(() => {
    if (!heldDraft || !schemeCheck) return;
    // Only release once PCRS can actually verify the panel (on/after the 1st).
    if (heldDraft.holdUntil && new Date(heldDraft.holdUntil) > new Date()) return;
    if (!DEFINITIVE_STATUSES.includes(schemeCheck.status)) return;
    // Post at most once per draft per mount.
    if (attemptedRef.current === heldDraft.id) return;
    attemptedRef.current = heldDraft.id;

    releaseDrafts([
      { patientId, schemeCheck: toReleaseSchemeCheck(schemeCheck) },
    ]);
  }, [heldDraft, schemeCheck, patientId, releaseDrafts]);

  return { heldDraft };
};
